import { type ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import authStore from 'store/auth/auth'
import elplanStore, { type ElplanDateView, setCurrentDate, setCurrentDateView } from 'store/elplan/elplan'
import NewSwitch from 'ui/atoms/NewSwitch/NewSwitch'
import { DatePicker } from 'ui/molecules/pickers/DatePicker/DatePicker'
import Datetime from 'utils/datetime/datetime'
import { useSnapshot } from 'valtio'
import styles from '../DayAheadTradingView.module.less'
import { confirmDayAheadBidTableLeave } from '../dayAheadBidTableLeave.helper'

function seedUnsetDateWithTomorrow(): void {
  if (!elplanStore.date.start_time) {
    const tomorrow = Datetime.getTomorrowDate()
    setCurrentDate(tomorrow.startTime, tomorrow.endTime)
  }
}

export function DayAheadDateSelector(): ReactNode {
  const { t } = useTranslation()

  const elplanSnap = useSnapshot(elplanStore)
  const { activeSystem } = useSnapshot(authStore)
  const selectedStartTime = elplanSnap.date.start_time ?? Datetime.getTomorrowDate().startTime
  const selectedEndTime = elplanSnap.date.end_time ?? Datetime.getTomorrowDate().endTime
  const currentView = elplanSnap.date.current_view ?? 'tomorrow'
  const isCustomDate = currentView === 'custom'
  const systemHasFiveDaysOptimizationPeriod = activeSystem?.default_optimization_period === 119 // 5 days * 24h - 1h

  useEffect(seedUnsetDateWithTomorrow, [])

  function onDateTimePickerChange(value: ISODateTime | null | undefined): void {
    if (!value) {
      return
    }
    if (confirmDayAheadBidTableLeave('date')) {
      setCurrentDate(Datetime.getStartOfDay(value), Datetime.getEndOfDay(value))
    }
  }

  function updateCurrentView(newCurrentView: ElplanDateView): void {
    if (newCurrentView === 'tomorrow') {
      if (confirmDayAheadBidTableLeave('date')) {
        setCurrentDate(Datetime.getTomorrowDate().startTime, Datetime.getTomorrowDate().endTime)
      }
      return
    }

    if (newCurrentView === 'today') {
      if (confirmDayAheadBidTableLeave('date')) {
        setCurrentDate(Datetime.getTodayDate().startTime, Datetime.getTodayDate().endTime)
      }
      return
    }

    if (newCurrentView === 'optimizationPeriod') {
      const endTime = systemHasFiveDaysOptimizationPeriod
        ? Datetime.addDays(Datetime.getISONow(0, false), 5)
        : Datetime.getDateOneWeekAhead()
      if (confirmDayAheadBidTableLeave('date')) {
        setCurrentDate(Datetime.getTodayDate().startTime, endTime)
      }
      return
    }

    if (newCurrentView === 'custom') {
      if (!Datetime.isSameDate(selectedStartTime, selectedEndTime)) {
        if (!confirmDayAheadBidTableLeave('date')) {
          return
        }
        setCurrentDate(Datetime.getStartOfDay(selectedStartTime), Datetime.getEndOfDay(selectedStartTime))
      }
      setCurrentDateView('custom')
      return
    }
  }

  return (
    <>
      <div className={styles.DayAheadTradingView_ViewSwitch}>
        <NewSwitch
          value={currentView}
          items={[
            { label: t('Custom'), value: 'custom' },
            { label: systemHasFiveDaysOptimizationPeriod ? t('5 days') : t('Week'), value: 'optimizationPeriod' },
            { label: t('Today'), value: 'today' },
            { label: t('Tomorrow'), value: 'tomorrow' },
          ]}
          onClick={(value) => {
            updateCurrentView(value as ElplanDateView)
          }}
          setNoTabActive={currentView === ''}
        />
      </div>
      {isCustomDate && (
        <div className={styles.DayAheadTradingView_DatePicker}>
          <DatePicker
            value={selectedStartTime}
            onChange={onDateTimePickerChange}
            label={t('Chosen date')}
            showWeekNumbers
            granularity={'day'}
            isRequired
          />
        </div>
      )}
    </>
  )
}
